import type { MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ArrowLeft,
  Sparkles,
  Palette,
  Megaphone,
  Zap,
  Target,
  Image,
  Microscope,
} from 'lucide-react';

import { get } from '~/lib/api';
import {
  describeBlueprintAccess,
  describePlanAi,
} from '~/lib/plan-descriptors';
import { queryKeys } from '~/lib/query-keys';
import type { BillingPlan } from '~/types/billing';

export const meta: MetaFunction = () => [
  { title: 'Pricing — Deleg8 Studio' },
  { name: 'description', content: 'Simple, transparent pricing for every creative team.' },
];

const DEFAULT_PLAN_STYLE: { badge?: string; accent: string; border: string; cta: string } = {
  accent: 'from-creative-500/20 to-creative-400/10',
  border: 'border-white/[0.08] hover:border-creative-500/40',
  cta: 'bg-white/10 hover:bg-white/15 text-white',
};

const PLAN_HIGHLIGHTS: Record<string, { badge?: string; accent: string; border: string; cta: string }> = {
  starter: {
    accent: 'from-creative-500/20 to-creative-400/10',
    border: 'border-white/[0.08] hover:border-creative-500/40',
    cta: 'bg-white/10 hover:bg-white/15 text-white',
  },
  growth: {
    badge: 'Most Popular',
    accent: 'from-accent-500/20 to-accent-400/10',
    border: 'border-accent-500/40 ring-1 ring-accent-500/20',
    cta: 'bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 text-white shadow-glow hover:brightness-110',
  },
  pro: {
    accent: 'from-heat-500/20 to-heat-400/10',
    border: 'border-white/[0.08] hover:border-heat-500/40',
    cta: 'bg-white/10 hover:bg-white/15 text-white',
  },
};

const LIMIT_LABELS = [
  { key: 'maxBrands', label: 'Brand profiles', icon: Palette, tip: 'Separate brands with their own DNA, voice, and visuals' },
  { key: 'maxCampaigns', label: 'Campaigns', icon: Megaphone, tip: 'Multi-channel campaigns with copy + visuals for every platform' },
  { key: 'maxGenerations', label: 'AI generation runs', icon: Zap, tip: 'Each run produces all the copy and images for a campaign — re-run to get fresh variations' },
  { key: 'maxCompetitors', label: 'Competitor profiles', icon: Target, tip: 'Competitors whose websites are scraped to inform your brand positioning' },
  { key: 'maxImageUploads', label: 'Reference images', icon: Image, tip: 'Upload mood boards, logos, or screenshots — AI extracts your design style from them' },
  { key: 'maxDeepAnalyses', label: 'Competitor deep dives', icon: Microscope, tip: 'Full AI analysis of a competitor\'s tone, positioning, and differentiation' },
] as const;

function buildCheckoutOnboardingLink(planKey?: string): string {
  return planKey ? `/checkout?plan=${planKey}` : '/checkout';
}

export default function PricingPage() {
  const { data: plans = [], isLoading } = useQuery({
    queryKey: queryKeys.billing.plans,
    queryFn: () => get<BillingPlan[]>('/billing/plans'),
    staleTime: 5 * 60 * 1000,
  });

  // Filter out trial plan
  const paidPlans = plans.filter((p) => p.key !== 'trial');

  return (
    <div className="min-h-screen bg-surface-950 text-white">
      {/* Nav */}
      <nav className="border-b border-white/[0.06] bg-surface-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Deleg8 Studio" className="h-8 w-8" loading="lazy" />
            <span className="text-lg font-bold">Deleg8 Studio</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-white/70 transition hover:text-white">
              Log in
            </Link>
            <Link
              to="/checkout"
              className="rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:brightness-110"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative px-4 pb-16 pt-16 sm:px-6 sm:pb-24 sm:pt-24">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -left-40 top-20 h-[400px] w-[400px] rounded-full bg-creative-700/20 blur-[100px]" />
        <div className="pointer-events-none absolute -right-32 top-10 h-[350px] w-[350px] rounded-full bg-accent-600/15 blur-[80px]" />

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-creative-400">
            Pricing
          </p>
          <h1 className="mt-3 text-3xl font-bold sm:text-5xl">
            Simple plans,{' '}
            <span className="bg-gradient-to-r from-creative-400 via-accent-400 to-heat-400 bg-clip-text text-transparent">
              powerful results.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/60 sm:text-lg">
            Start creating campaigns in minutes. Upgrade anytime as your needs grow.
          </p>
        </div>

        {/* Plan cards */}
        <div className="relative z-10 mx-auto mt-16 grid max-w-5xl gap-6 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[520px] animate-pulse rounded-2xl border border-white/[0.08] bg-white/[0.03]"
                />
              ))
            : paidPlans.map((plan) => {
                const style = PLAN_HIGHLIGHTS[plan.key] ?? DEFAULT_PLAN_STYLE;
                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col overflow-hidden rounded-2xl border bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.06] sm:p-8 ${style.border}`}
                  >
                    {/* Badge */}
                    {style.badge && (
                      <div className="absolute right-4 top-4 rounded-full bg-accent-500/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-accent-300">
                        {style.badge}
                      </div>
                    )}

                    {/* Plan name + price */}
                    <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">${plan.price}</span>
                      <span className="text-sm text-white/50">/mo</span>
                    </div>

                    {/* AI tier */}
                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/[0.06] px-3 py-2">
                      <Sparkles className="h-4 w-4 text-creative-400" />
                      <span className="text-xs font-medium text-white/70">
                        {describePlanAi(plan)}
                      </span>
                    </div>

                    {/* Limits */}
                    <ul className="mt-6 flex-1 space-y-3">
                      {LIMIT_LABELS.map(({ key, label, icon: Icon, tip }) => {
                        const value = plan.limits[key as keyof typeof plan.limits];
                        return (
                          <li key={key} className="group flex items-center gap-3 text-sm">
                            <Icon className="h-4 w-4 flex-shrink-0 text-white/30" />
                            <span className="relative text-white/70">
                              {label}
                              {tip && (
                                <span className="pointer-events-none absolute -top-8 left-0 z-20 hidden w-max max-w-[200px] rounded-lg bg-surface-800 px-2.5 py-1.5 text-xs text-white/80 shadow-lg group-hover:block">
                                  {tip}
                                </span>
                              )}
                            </span>
                            <span className="ml-auto font-semibold text-white">
                              {typeof value === 'number' ? value : '--'}
                            </span>
                          </li>
                        );
                      })}
                      <li className="flex items-center gap-3 text-sm">
                        <Check className="h-4 w-4 flex-shrink-0 text-emerald-400" />
                        <span className="text-white/70">
                          {describeBlueprintAccess(
                            plan.limits.allowedBlueprintKeys,
                          )}
                        </span>
                      </li>
                    </ul>

                    {/* CTA */}
                    <Link
                      to={buildCheckoutOnboardingLink(plan.key)}
                      className={`mt-6 block w-full rounded-xl px-4 py-3 text-center text-sm font-semibold transition ${style.cta}`}
                    >
                      Select {plan.name}
                    </Link>
                  </div>
                );
              })}
        </div>

        {/* Back link */}
        <div className="mx-auto mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 transition hover:text-white/80"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
